import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Collapse from "@mui/material/Collapse";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import { UNIFIED_DASHBOARD_PATH } from "components/allInOneDashboard/constants/productRouteMap";

const ACCENT = "#4E63A4";
const FACT_DOT = "#4E63A4";
const FACT_BORDER = "#E2E8F0";
const HERO_GRADIENT =
  "linear-gradient(156deg, #050D1F 0%, #0A2255 50%, #1A4080 100%)";
const HERO_GRADIENT_MOBILE =
  "linear-gradient(136deg, #050D1F 0%, #0A2255 50%, #1A4080 100%)";
const CTA_GRADIENT = "linear-gradient(270deg, #60A5FA 0%, #2563EB 100%)";
const CALENDLY_DEEP_DIVE =
  "https://calendly.com/primesupport-jarvisinvest/30min";

const FOR_YOU_GRADIENT = "linear-gradient(-26deg, #3B82F6 1.4%, #1E3A5F 98.6%)";
const HERO_CARD_BG = "#1160C7";
const FEATURE_BORDER = "#E5E7EB";

const img = (name) =>
  `/images/US-product-info/${encodeURIComponent(name)}`;

const ICONS = {
  hero: img("image 10.png"),
  frameWhite: img("Frame-white.svg"),
  frame: img("Frame.svg"),
  check: img("icon.svg"),
  anchor: img("typographic-anchor.svg"),
  benefit1: img("icon-circle.svg"),
  benefit2: img("icon-circle (1).svg"),
  benefit3: img("icon-circle (2).svg"),
  tech1: img("technology-_1_ 1.svg"),
  dashboard: img("dashboard 1.svg"),
  clock: img("clock 2.svg"),
  tech2: img("technology-_2_ 1.svg"),
  tracking: img("tracking 1.svg"),
  momentum: img("momentum 1.svg"),
  consistency: img("Group.svg"),
  noStopping: img("Vector.svg"),
};

const BENEFIT_TRAY = [
  {
    icon: ICONS.benefit1,
    label: "Exposure to equities, commodities & fixed income",
  },
  { icon: ICONS.benefit2, label: "Automated Execution" },
  {
    icon: ICONS.benefit3,
    label: "Powered by Proprietary AI Research",
  },
];

const FEATURE_CARDS = [
  {
    icon: ICONS.tech1,
    text: "Every stock is selected using Jarvis Proprietary AI research framework.",
  },
  {
    icon: ICONS.dashboard,
    text: "Invest in industry leaders with strong fundamentals and long-term growth potential.",
  },
  {
    icon: ICONS.clock,
    text: "Exposure across multiple sectors to reduce concentration risk.",
  },
  {
    icon: ICONS.tech2,
    text: "Portfolios are reviewed periodically to adapt to changing market conditions.",
  },
  {
    icon: ICONS.tracking,
    text: "Diversify beyond equities with commodities and fixed income.",
  },
  {
    icon: ICONS.momentum,
    text: "Reduce dependence on a single market through international exposure.",
  },
  {
    icon: ICONS.consistency,
    text: "Designed for investors focused on compounding wealth over time.",
  },
  {
    icon: ICONS.noStopping,
    text: "A disciplined investment strategy designed to help investors participate in the long-term growth of the US economy.",
  },
  {
    icon: ICONS.noStopping,
    text: "Capture opportunities in the US market while adding dollar exposure to help diversify and strengthen your long-term portfolio.",
  },
];

const FOR_YOU_CARDS = [
  {
    label: "Global Diversification",
    description: "Expand beyond Indian markets",
  },
  {
    label: "Long-Term Investors",
    description: "Focused on sustainable wealth creation.",
  },
  {
    label: "AI-Driven Investing",
    description: "Data-backed investment decisions.",
  },
  {
    label: "US Market Exposure",
    description: "Gain Exposure to US market’s top performing companies",
  },
  {
    label: "Dollar Diversification",
    description: "Build wealth across global currencies.",
  },
  {
    label: "Managed Portfolio",
    description: "Professionally curated and monitored.",
  },
];

const QUICK_FACTS = [
  { label: "Market", value: "United States" },
  { label: "Strategy", value: "AI Powered" },
  { label: "Investment Style", value: "Long Term" },
  { label: "Portfolio", value: "Diversified" },
  { label: "Monitoring", value: "Continuous" },
  { label: "Investor Type", value: "Wealth Builders" },
];

const MOBILE_ABOUT_ITEMS = [
  {
    icon: ICONS.frame,
    text: "Gain access to carefully selected US companies backed by AI-driven research.",
  },
  ...FEATURE_CARDS.map(({ icon, text }) => ({ icon, text })),
];

const MOBILE_FOR_YOU = [
  "Looking to diversify beyond Indian markets",
  "Focused on long-term sustainable wealth creation",
  "Prefer data-backed, AI-driven investment decisions",
  "Want exposure to top-performing US companies",
  "Seeking dollar diversification across currencies",
  "Prefer a professionally curated and monitored portfolio",
];

const MOBILE_ABOUT_PREVIEW = 2;

const pagePad = {
  px: { xs: 2.5, md: 7.5 },
  maxWidth: 1320,
  mx: "auto",
  width: "100%",
};

const FeatureCard = ({ icon, text }) => (
  <Stack
    spacing={1.25}
    sx={{
      p: 3,
      height: "100%",
      boxSizing: "border-box",
      borderRadius: "20px",
      bgcolor: "#fff",
      border: `1px solid ${FEATURE_BORDER}`,
      boxShadow: "0 6px 9px rgba(0,0,0,0.05)",
    }}
  >
    <Box component="img" src={icon} alt="" sx={{ width: 28, height: 28 }} />
    <Text sx={{ fontSize: 16, fontWeight: 500, color: "#111827", lineHeight: "24px" }}>
      {text}
    </Text>
  </Stack>
);

const ChecklistCard = ({ label, description }) => (
  <Stack
    spacing="12px"
    sx={{
      p: "24px",
      height: "100%",
      minWidth: 0,
      width: "100%",
      boxSizing: "border-box",
      borderRadius: "12px",
      bgcolor: "#fff",
      border: "1px solid #D5DEFB",
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: -1,
        right: -1,
        height: 3,
        bgcolor: ACCENT,
      }}
    />
    <Stack direction="row" spacing="12px" alignItems="center" sx={{ width: "100%" }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: "12px",
          bgcolor: "rgba(11,62,130,0.05)",
          border: "1px solid rgba(11,62,130,0.2)",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box
          component="img"
          src={ICONS.check}
          alt=""
          sx={{ width: 18, height: 18, display: "block" }}
        />
      </Box>
      <Text
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 15,
          fontWeight: 600,
          color: "#374151",
          lineHeight: "20px",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {label}
      </Text>
    </Stack>
    <Text
      sx={{
        fontSize: 13,
        fontWeight: 400,
        color: "#313131",
        lineHeight: "20px",
        fontFamily: "'DM Sans', sans-serif",
        width: "100%",
        whiteSpace: "normal",
        overflowWrap: "break-word",
      }}
    >
      {description}
    </Text>
  </Stack>
);

const FactCard = ({ label, value }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 3,
      minHeight: 114,
      height: "100%",
      boxSizing: "border-box",
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: FACT_DOT,
          flexShrink: 0,
        }}
      />
      <Text sx={{ fontSize: 18, fontWeight: 500, color: "#6B7280", lineHeight: "23px" }}>
        {label}
      </Text>
    </Stack>
    <Text sx={{ fontSize: 24, fontWeight: 700, color: "#0F172A", lineHeight: "31px" }}>
      {value}
    </Text>
  </Stack>
);

const MobileAboutRow = ({ icon, text }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box
      component="img"
      src={icon}
      alt=""
      sx={{ width: 20, height: 20, flexShrink: 0, mt: "2px" }}
    />
    <Text sx={{ fontSize: 14, color: "#475569", lineHeight: "21px" }}>{text}</Text>
  </Stack>
);

const MobileForYouRow = ({ text }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    sx={{
      p: 2,
      minHeight: 63,
      borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 10px 12px rgba(0,0,0,0.1)",
      backdropFilter: "blur(7px)",
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#BFDBFE" }} />
    </Box>
    <Text sx={{ fontSize: 14, fontWeight: 500, color: "#fff", lineHeight: 1.35 }}>{text}</Text>
  </Stack>
);

const MobileFactTile = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      flex: 1,
      minWidth: 0,
      minHeight: 90,
      p: 2,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 12, color: "#000", lineHeight: "16px" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
      {value}
    </Text>
  </Stack>
);

const UsStrategyProductInfo = () => {
  const history = useHistory();
  const [aboutExpanded, setAboutExpanded] = useState(false);

  const handleBack = () => history.push(UNIFIED_DASHBOARD_PATH);

  const handleScheduleDeepDive = () => {
    window.open(CALENDLY_DEEP_DIVE, "_blank", "noopener,noreferrer");
  };

  const aboutPreview = MOBILE_ABOUT_ITEMS.slice(0, MOBILE_ABOUT_PREVIEW);
  const aboutRest = MOBILE_ABOUT_ITEMS.slice(MOBILE_ABOUT_PREVIEW);
  const row2 = FEATURE_CARDS.slice(1, 4);
  const row3 = FEATURE_CARDS.slice(4, 7);
  const row4 = FEATURE_CARDS.slice(7);

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", pb: 0 }}>
      {/* Hero — desktop + mobile (Figma 8813:71114) */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: { xs: HERO_GRADIENT_MOBILE, md: HERO_GRADIENT },
          px: { xs: 3, md: 0 },
          pt: { xs: 2.5, md: 4 },
          pb: { xs: 0, md: 0 },
          minHeight: { md: 640 },
          borderRadius: { xs: "0 0 24px 24px", md: 0 },
          boxShadow: {
            xs: "0 12px 28px -10px rgba(5,13,31,0.35)",
            md: "none",
          },
        }}
      >
        <Box sx={{ ...pagePad, position: "relative", zIndex: 1, px: { xs: 0, md: 7.5 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: { xs: 2.5, md: 5 } }}>
            <IconButton
              onClick={handleBack}
              aria-label="Back to Explore"
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.2)",
                border: "1px solid #C3C3C3",
                color: "#fff",
                p: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
            </IconButton>
            <Box>
              <Text
                sx={{
                  fontSize: { xs: 18, md: 32 },
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: { xs: "24px", md: 1.2 },
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Jarvis US Strategy
              </Text>
              <Text
                sx={{
                  fontSize: 14,
                  color: "rgba(255,255,255,0.7)",
                  mt: { xs: 0.25, md: 0.5 },
                  lineHeight: { xs: "21px", md: "20px" },
                }}
              >
                Investing powered by AI
              </Text>
            </Box>
          </Stack>

          {/* Desktop hero */}
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={4}
            sx={{ display: { xs: "none", md: "flex" }, pb: 4 }}
          >
            <Stack spacing={0} sx={{ flex: 1, maxWidth: 640, pt: 2, overflow: "visible" }}>
              <Text
                sx={{
                  fontSize: 64,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "76px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                A Smarter Way to Invest in US Markets.
              </Text>
              {/* Figma 8813:63669 — Subheadline 827×90 (3 × 30px line-height) */}
              <Box sx={{ mt: 3, width: 827, maxWidth: "none" }}>
                <Text
                  sx={{
                    fontSize: 20,
                    color: "#fff",
                    lineHeight: "30px",
                    whiteSpace: "nowrap",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  US Strategy is a professionally managed portfolio built to create long-term wealth
                </Text>
                <Text
                  sx={{
                    fontSize: 20,
                    color: "#fff",
                    lineHeight: "30px",
                    whiteSpace: "nowrap",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  through carefully selected US equities, complemented by commodities for diversification
                </Text>
                <Text
                  sx={{
                    fontSize: 20,
                    color: "#fff",
                    lineHeight: "30px",
                    whiteSpace: "nowrap",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  and inflation protection, and fixed income for capital preservation.
                </Text>
              </Box>
              <Button
                onClick={handleScheduleDeepDive}
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  mt: 3.5,
                  alignSelf: "flex-start",
                  px: 4,
                  py: 2.25,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#fff",
                  background: CTA_GRADIENT,
                  border: "1px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 10px 12px rgba(37,99,235,0.25)",
                  "&:hover": { background: CTA_GRADIENT, opacity: 0.95 },
                }}
              >
                Schedule a Deep Dive Call
              </Button>

              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  mt: 2.5,
                  px: 2.5,
                  py: 2,
                  maxWidth: 651,
                  borderRadius: "16px",
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.2)",
                  backdropFilter: "blur(8px)",
                }}
              >
                {BENEFIT_TRAY.map((item, index) => (
                  <React.Fragment key={item.label}>
                    {index > 0 ? (
                      <Box
                        sx={{
                          width: "1px",
                          alignSelf: "stretch",
                          bgcolor: "rgba(255,255,255,0.2)",
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        component="img"
                        src={item.icon}
                        alt=""
                        sx={{ width: 28, height: 28, flexShrink: 0 }}
                      />
                      <Text
                        sx={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: "#AFCEFF",
                          lineHeight: "14px",
                        }}
                      >
                        {item.label}
                      </Text>
                    </Stack>
                  </React.Fragment>
                ))}
              </Stack>
            </Stack>
            <Box
              component="img"
              src={ICONS.hero}
              alt="Jarvis US Strategy"
              sx={{
                width: 420,
                maxWidth: "42%",
                objectFit: "contain",
                filter: "drop-shadow(-7.5px 3px 26px rgba(0,0,0,0.25))",
                mt: 2,
              }}
            />
          </Stack>

          {/* Mobile hero — Figma 8813:71114 / image 8813:71120 */}
          <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
            <Text
              sx={{
                fontSize: 30,
                fontWeight: 800,
                color: "#fff",
                lineHeight: "39px",
                textShadow: "0 4px 14px rgba(0,0,0,0.5)",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              A Smarter Way to Invest in US Markets
            </Text>
            <Text
              sx={{
                fontSize: 14,
                color: "rgba(255,255,255,0.85)",
                lineHeight: "21px",
                textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              US Strategy is a professionally managed portfolio built for long-term wealth through
              carefully selected US equities, complemented by commodities for diversification and
              fixed income for capital preservation. Powered by Jarvis&apos; Proprietary AI Research
              Framework and backed by institutional-grade research.
            </Text>

            {/* Full-bleed bottom stage so image left:-49 matches Figma hero frame */}
            <Box
              sx={{
                position: "relative",
                mt: 1,
                mx: -3,
                width: "calc(100% + 48px)",
                height: 216,
              }}
            >
              {/* Globe — Figma 8813:71120 @ left -49 / 247×216 */}
              <Box
                sx={{
                  position: "absolute",
                  left: -49,
                  bottom: 0,
                  width: 247,
                  height: 216,
                  overflow: "hidden",
                  pointerEvents: "none",
                  filter: "drop-shadow(-7.5px 3px 26.25px rgba(0,0,0,0.25))",
                }}
              >
                <Box
                  component="img"
                  src={ICONS.hero}
                  alt="Jarvis US Strategy"
                  sx={{
                    position: "absolute",
                    left: "-3.32%",
                    top: "-15.35%",
                    width: "106.69%",
                    height: "122%",
                    maxWidth: "none",
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              </Box>

              {/* CTA — compact beside globe (Figma 8813:74827) */}
              <Button
                onClick={handleScheduleDeepDive}
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
                sx={{
                  position: "absolute",
                  left: "auto",
                  right: 32,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "auto",
                  maxWidth: 168,
                  minHeight: 42,
                  height: "auto",
                  px: 1.75,
                  py: 1,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  lineHeight: 1.2,
                  color: "#fff",
                  whiteSpace: "normal",
                  textAlign: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  background: CTA_GRADIENT,
                  border: "1.5px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 10px 12px rgba(3,44,177,0.2)",
                  "&:hover": { background: CTA_GRADIENT, opacity: 0.95 },
                  "& .MuiButton-endIcon": { ml: 0.5, mr: 0, flexShrink: 0 },
                  "& .MuiButton-label, &": {
                    alignItems: "center",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                >
                  Schedule a
                  <br />
                  Deep Dive Call
                </Box>
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* About — desktop */}
      <Box sx={{ ...pagePad, py: 7.5, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 56,
            fontWeight: 700,
            color: "#111827",
            letterSpacing: "-0.02em",
            lineHeight: "64px",
            mb: 6,
          }}
        >
          About US Strategy
        </Text>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
            gap: 2,
            mb: 2,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              px: 4,
              pt: 4,
              pb: 3,
              height: 236,
              boxSizing: "border-box",
              borderRadius: "20px",
              bgcolor: HERO_CARD_BG,
              boxShadow: "0 10px 12px rgba(0,0,0,0.1)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ flexShrink: 0, minHeight: 32 }}
            >
              <Box component="img" src={ICONS.frameWhite} alt="" sx={{ width: 32, height: 32 }} />
              <Text
                sx={{
                  bgcolor: "#fff",
                  px: 2,
                  py: 0.5,
                  borderRadius: "14px",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#1F351D",
                  whiteSpace: "nowrap",
                }}
              >
                Currently AI under reinforcement stage
              </Text>
            </Stack>
            <Text
              sx={{
                mt: 1.25,
                fontSize: { md: "clamp(52px, 6.2vw, 96px)" },
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1,
                letterSpacing: "-0.02em",
                whiteSpace: "nowrap",
              }}
            >
              Beyond Borders.
            </Text>
            <Text
              sx={{
                mt: 1.25,
                fontSize: { md: 18, lg: 22 },
                fontWeight: 500,
                color: "rgba(255,255,255,0.9)",
                lineHeight: "26px",
                maxWidth: "95%",
              }}
            >
              Gain access to carefully selected US companies backed by AI-driven research.
            </Text>
          </Box>
          <FeatureCard {...FEATURE_CARDS[0]} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
            alignItems: "stretch",
            "& > *": { minHeight: 160 },
          }}
        >
          {row2.map((item) => (
            <FeatureCard key={item.text} {...item} />
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
            alignItems: "stretch",
            "& > *": { minHeight: 140 },
          }}
        >
          {row3.map((item) => (
            <FeatureCard key={item.text} {...item} />
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            alignItems: "stretch",
            "& > *": { minHeight: 140 },
          }}
        >
          {row4.map((item) => (
            <FeatureCard key={item.text} {...item} />
          ))}
        </Box>
      </Box>

      {/* About — mobile */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2}
          sx={{
            p: 2.5,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            About US Strategy
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Stack spacing={2.5}>
            {aboutPreview.map((item, index) => (
              <MobileAboutRow key={`about-preview-${index}`} {...item} />
            ))}
          </Stack>
          <Collapse in={aboutExpanded}>
            <Stack spacing={2.5} sx={{ pt: 2.5 }}>
              {aboutRest.map((item, index) => (
                <MobileAboutRow key={`about-rest-${index}`} {...item} />
              ))}
            </Stack>
          </Collapse>
          <Box
            component="button"
            type="button"
            onClick={() => setAboutExpanded((v) => !v)}
            sx={{
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              p: 0,
              alignSelf: "flex-start",
            }}
          >
            <Text sx={{ fontSize: 14, fontWeight: 500, color: "#2563EB", lineHeight: "24px" }}>
              {aboutExpanded ? "read less" : "read more"}
            </Text>
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — desktop (Figma 8813:63760 / bg-lines 8813:63763) */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          px: { md: "48px", lg: "64px", xl: "80px" },
          py: "96px",
          bgcolor: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative washes */}
        <Box
          sx={{
            position: "absolute",
            bottom: -120,
            right: -120,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(78,99,164,0.1) 0%, rgba(78,99,164,0.03) 45%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: -220,
            right: -180,
            width: 720,
            height: 720,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(78,99,164,0.08) 0%, rgba(78,99,164,0.02) 50%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        {/* Figma bg-lines 8813:63763 — #4E63A4 @ 6% */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.06,
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {[120, 360, 600, 840, 1080, 1320].map((left) => (
            <Box
              key={`v-${left}`}
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left,
                width: "1px",
                bgcolor: "#4E63A4",
              }}
            />
          ))}
          {[141, 320, 500, 680, 860].map((top) => (
            <Box
              key={`h-${top}`}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top,
                height: "1px",
                bgcolor: "#4E63A4",
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 1320,
            mx: "auto",
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: { md: "32px", lg: "48px" },
            minHeight: 578,
          }}
        >
          {/* Left column — kept compact so checklist cards get ~360px+ each */}
          <Stack
            spacing="32px"
            sx={{
              flex: "0 1 360px",
              maxWidth: { md: 320, lg: 380 },
              minWidth: 240,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "36px",
                border: `1.5px solid ${ACCENT}`,
                display: "grid",
                placeItems: "center",
                bgcolor: "#fff",
                boxShadow: "0 10px 12px rgba(0,0,0,0.04)",
              }}
            >
              <Text
                sx={{
                  fontSize: 40,
                  fontWeight: 800,
                  color: ACCENT,
                  lineHeight: "40px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                ?
              </Text>
            </Box>
            <Text
              sx={{
                fontSize: { md: 56, lg: 72, xl: 80 },
                fontWeight: 900,
                color: "#1A1A1A",
                lineHeight: { md: "64px", lg: "80px", xl: "88px" },
                letterSpacing: "-1.6px",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Is This
              <br />
              For You?
            </Text>
            <Text
              sx={{
                fontSize: 18,
                fontWeight: 400,
                color: "#6B7280",
                lineHeight: "28px",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              A quick checklist to determine whether US Strategy aligns with your investment
              goals.
            </Text>
          </Stack>

          {/* Right column — wider cards so subtext fits (Figma cards ≈360px) */}
          <Stack spacing="16px" sx={{ flex: "1 1 780px", minWidth: 0, maxWidth: "100%" }}>
            {[0, 2, 4].map((start) => (
              <Box
                key={`for-you-row-${start}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "16px",
                  width: "100%",
                  alignItems: "stretch",
                }}
              >
                {FOR_YOU_CARDS.slice(start, start + 2).map((card) => (
                  <ChecklistCard key={card.label} {...card} />
                ))}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Is This For You — mobile (Protect / Atlas pattern) */}
      <Box sx={{ px: 3, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "24px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(255,255,255,0.15)",
            background: FOR_YOU_GRADIENT,
            boxShadow:
              "0 0 18px rgba(59,130,246,0.15), 0 10px 24px -10px rgba(30,58,95,0.3), 0 18px 40px -12px rgba(0,0,0,0.15)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -110,
              right: -90,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(96,165,250,0.45) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -120,
              right: 40,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(37,99,235,0.4) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Stack spacing={1.25} sx={{ position: "relative" }}>
            <Text sx={{ fontSize: 18, fontWeight: 700, color: "#E6E6E6", lineHeight: "24px" }}>
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: "18px" }}>
              A quick checklist to determine whether US Strategy aligns with your investment
              goals.
            </Text>
          </Stack>
          <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.2)", position: "relative" }} />
          <Stack spacing={1.5} sx={{ position: "relative" }}>
            {MOBILE_FOR_YOU.map((text) => (
              <MobileForYouRow key={text} text={text} />
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* Quick Facts — desktop (Figma 8813:63833) */}
      <Box sx={{ ...pagePad, py: 7.5, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 40,
            fontWeight: 800,
            color: "#0F172A",
            lineHeight: "52px",
            mb: 6,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Quick Facts
        </Text>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2.5,
          }}
        >
          {QUICK_FACTS.map((fact) => (
            <FactCard key={`${fact.label}-${fact.value}`} {...fact} />
          ))}
        </Box>
      </Box>

      {/* Quick Facts — mobile (Protect / Atlas pattern) */}
      <Box sx={{ px: 3, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "26px" }}>
            Quick Facts
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            {QUICK_FACTS.map((fact) => (
              <MobileFactTile key={`${fact.label}-${fact.value}`} {...fact} />
            ))}
          </Box>
        </Stack>
      </Box>

      <AllInOneFooter />
    </Box>
  );
};

export default UsStrategyProductInfo;
